import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Languages, Megaphone, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/components/forms/Select'
import { Button } from '@/ui/components/common/Button'
import { useGetBotSettings, useUpdateBotSettings } from '@/application/services/settings.service'
import { apiClient } from '@/infrastructure/api/client'

export function BotSettingsPanel() {
  const { t } = useTranslation()
  const { data: settings, isLoading } = useGetBotSettings()
  const updateSettings = useUpdateBotSettings()
  const [sendingWelcome, setSendingWelcome] = useState(false)

  const handleLanguageChange = async (lang: string) => {
    const validLang: 'en' | 'fr' = lang === 'fr' ? 'fr' : 'en'
    try {
      await updateSettings.mutateAsync({ language: validLang })
      toast.success(t('admin.twitch_language_saved'))
    } catch {
      toast.error(t('common.error'))
    }
  }

  const handleSendWelcome = async () => {
    setSendingWelcome(true)
    try {
      await Promise.allSettled([
        apiClient.post('/auth/twitch/welcome'),
        apiClient.post('/auth/youtube/welcome'),
      ])
      toast.success(t('admin.bot_welcome_sent'))
    } catch {
      toast.error(t('common.error'))
    } finally {
      setSendingWelcome(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
        <RefreshCw className="w-4 h-4 animate-spin" />
        Loading settings...
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Language selector */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Languages className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">{t('admin.twitch_language_label')}</span>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={settings?.language ?? 'fr'}
            onValueChange={handleLanguageChange}
            disabled={updateSettings.isPending}
          >
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">{t('admin.twitch_language_en')}</SelectItem>
              <SelectItem value="fr">{t('admin.twitch_language_fr')}</SelectItem>
            </SelectContent>
          </Select>
          {updateSettings.isPending && (
            <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />
          )}
        </div>
        <p className="text-xs text-muted-foreground">{t('admin.twitch_language_desc')}</p>
      </div>

      {/* Send welcome message */}
      <div className="space-y-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleSendWelcome}
          isLoading={sendingWelcome}
          leftIcon={<Megaphone className="w-4 h-4" />}
        >
          {t('admin.bot_send_welcome')}
        </Button>
        <p className="text-xs text-muted-foreground">{t('admin.bot_send_welcome_desc')}</p>
      </div>
    </div>
  )
}
